import { useGet } from "@albertine/api-client";
import type { Supplier } from "@albertine/types";
import type {
    CatalogItem as CatalogItemApi,
    CatalogItemFull,
    ListThemesResponseData,
    Theme,
} from "@albertine/types/openapi/lmt";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useHistory } from "react-router";
import type { Agent } from "../../common/types/Agent";
import type { ThemeQueryFilters } from "../../common/types/DynamicTheme";
import { EventEmitter } from "../../EventEmitter";
import { useClaims } from "../../utils/claims.util";
import type { ParsedThemeUrlState } from "../../utils/customHook.util";
import routes from "../../utils/routes";
import {
    filterThemeByPublished,
    takeIfCityguideLocationMatches,
    takeIfLocationMatches,
    takeIfNameMatches,
    takeIfThemePreferenceMatches,
} from "../../utils/themes.util";
import { logErrorAndShowToast, scrollToElement } from "../../utils/window.util";
import EditCatalogItem from "../CatalogBeta/EditCatalogItem";
import ViewCatalogItem from "../CatalogBeta/ViewCatalogItem";
import WithGoogle from "../CatalogBeta/WithGoogle";
import Button from "../Common/Buttons";
import { Flex } from "../Common/Flex";
import Icon from "../Common/Icon";
import Label from "../Common/Label";
import { List } from "../Common/List";
import FullScreenOverlay from "../Common/Overlay/FullScreenOverlay";
import Tag from "../Common/Tag";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import EditTheme from "./EditTheme";
import PreviewThemes from "./PreviewThemes";
import ThemeForm from "./StaticThemeForm";
import ThemeFilters from "./ThemeFilters";
import ThemeList, {
    type GroupedThemes,
    type ThemeGroupPartition,
    ThemeListLoading,
} from "./ThemeList";
import { createThemeAnchor } from "./ThemeView";
import "./ContentThemes.css";

function getFilteredThemes(
    themes: Theme[] | undefined,
    queryParams: ThemeQueryFilters,
) {
    if (!themes) {
        return {
            spotlightThemes: undefined,
            cityGuideThemes: undefined,
            ungrouped: undefined,
            themeGroups: undefined,
        };
    }

    const spotlightThemes = themes
        .filter((t) => t.placement === "spotlight")
        .filter(takeIfNameMatches(queryParams.nameSearchTerm))
        .filter(takeIfLocationMatches(queryParams.location))
        .filter(takeIfThemePreferenceMatches(queryParams.preferences))
        .filter(filterThemeByPublished(queryParams.published));
    const cityGuideThemes = themes
        .filter((t) => t.placement === "cityGuide")
        .filter(takeIfNameMatches(queryParams.nameSearchTerm))
        .filter(takeIfCityguideLocationMatches(queryParams.location))
        .filter(takeIfThemePreferenceMatches(queryParams.preferences))
        .filter(filterThemeByPublished(queryParams.published));
    const otherThemes = themes
        .filter(
            (t) =>
                t.placement !== "spotlight" &&
                t.placement !== "cityGuide" &&
                t.placement !== "category",
        )
        .filter(takeIfNameMatches(queryParams.nameSearchTerm))
        .filter(takeIfLocationMatches(queryParams.location))
        .filter(takeIfThemePreferenceMatches(queryParams.preferences))
        .filter(filterThemeByPublished(queryParams.published))
        .reduce<Record<ThemeGroupPartition, GroupedThemes>>(
            (prev, current) => {
                if (current.group?.id) {
                    const newItem: GroupedThemes = {
                        group: current.group,
                        themes: [...(prev[current.group.id]?.themes || []), current],
                    };

                    return {
                        ...prev,
                        [current.group.id]: newItem,
                    };
                }

                const newItem: GroupedThemes = {
                    group: null,
                    themes: [...prev.ungrouped.themes, current],
                };

                return {
                    ...prev,
                    ungrouped: newItem,
                };
            },
            {
                ungrouped: {
                    group: null,
                    themes: [],
                },
            },
        );

    const { ungrouped, ...themeGroups } = otherThemes;

    return { spotlightThemes, cityGuideThemes, ungrouped, themeGroups };
}

function ContentThemes(props: {
    currentAgent: Agent;
    suppliers: Supplier[];
    pathParams: ParsedThemeUrlState;
    queryParams: ThemeQueryFilters;
}) {
    const { currentAgent, suppliers, pathParams, queryParams } = props;
    const history = useHistory();
    const { isAdmin } = useClaims();
    const queryClient = useQueryClient();

    const themesQuery = useGet("listThemes", {});

    useEffect(() => {
        if (themesQuery.isError) {
            logErrorAndShowToast(themesQuery.error as Error);
        }
    }, [themesQuery.isError, themesQuery.error]);

    function returnToThemes() {
        history.push(routes.themes());
    }

    function goToArticleView(themeId: string, articleId: string) {
        history.push(routes.themesViewArticle(themeId, articleId));
    }

    function goToArticleEdit(themeId: string, catalogItem: CatalogItemApi) {
        history.push(routes.themesEditArticle(themeId, catalogItem.id));
    }

    function goToThemeCreate() {
        history.push(routes.themesCreateItem());
    }

    function goToThemeEdit(themeId: string) {
        history.push(routes.themesEditItem(themeId));
    }

    function goToMemberPreview() {
        history.push(routes.themesPreview());
    }

    async function refreshThemes(callback?: () => void) {
        await queryClient.invalidateQueries({ queryKey: ["listThemes"] });
        callback?.();
    }

    function onArticleEdit(editedItem: CatalogItemFull) {
        setTimeout(() => {
            refreshThemes();
        }, 100);

        EventEmitter.emit({
            name: "showToastMessage",
            params: {
                message: `"${editedItem.name}" successfully updated`,
                type: "info",
            },
        });
    }

    function onArticleArchiveToggle() {
        setTimeout(() => {
            refreshThemes(() => {
                returnToThemes();
            });
        }, 1000);
    }

    function onThemeEdit(theme: Theme) {
        queryClient.setQueryData<ListThemesResponseData>(
            ["listThemes", {}],
            (oldData) =>
                oldData
                    ? {
                          ...oldData,
                          results: oldData.results.map((t) =>
                              t.id === theme.id ? theme : t,
                          ),
                      }
                    : oldData,
        );
        queryClient.setQueryData(["theme", { param: { themeId: theme.id } }], theme);
        void queryClient.invalidateQueries({
            queryKey: ["lmtCatalogThemeCatalogItems", { param: { themeId: theme.id } }],
        });
        returnToThemes();

        EventEmitter.emit({
            name: "showToastMessage",
            params: {
                message: `Theme "${theme.name}" updated successfully`,
                type: "info",
            },
        });
    }

    function onThemeCreate(theme: Theme) {
        refreshThemes(() => {
            scrollToElement(createThemeAnchor({ id: theme.id }), true);
        });
        returnToThemes();

        EventEmitter.emit({
            name: "showToastMessage",
            params: {
                message: `Theme "${theme.name}" created successfully`,
                type: "info",
            },
        });
    }

    const themesData = themesQuery.data;
    const { spotlightThemes, cityGuideThemes, themeGroups, ungrouped } =
        getFilteredThemes(themesData?.results, queryParams);
    const tenantPointOfView =
        queryParams.tenantPointOfView === "global"
            ? undefined
            : queryParams.tenantPointOfView;

    return (
        <>
            <List.Tab>
                <List.TabHeader>
                    <Flex.Row alignItems="center" justifyContent="space-between">
                        <div>
                            <Flex.Row gap="medium" alignItems="center">
                                <h2>Content themes</h2>

                                {themesData ? (
                                    <Tag.Medium.N90>
                                        {themesData?.results.length}
                                    </Tag.Medium.N90>
                                ) : (
                                    <LoadingSpinner />
                                )}
                            </Flex.Row>
                        </div>

                        <div>
                            <Flex.Row gap="medium">
                                <Button
                                    onClick={() => {
                                        goToMemberPreview();
                                    }}
                                >
                                    <Icon.Search.Medium.P50 />
                                    <Label.Medium.SemiBold.N10>
                                        Preview as
                                    </Label.Medium.SemiBold.N10>
                                </Button>
                                {isAdmin && (
                                    <Button
                                        onClick={() => {
                                            goToThemeCreate();
                                        }}
                                        className="content-theme__add-new-button"
                                    >
                                        <Icon.Add.Medium.P50 />
                                        <Label.Medium.SemiBold.N10>
                                            New theme
                                        </Label.Medium.SemiBold.N10>
                                    </Button>
                                )}
                            </Flex.Row>
                        </div>
                    </Flex.Row>
                </List.TabHeader>

                <ThemeFilters />

                {spotlightThemes && cityGuideThemes ? (
                    <ThemeList
                        articlesSource={{
                            type: "tenantContent",
                            tenant: tenantPointOfView,
                        }}
                        spotlightThemes={spotlightThemes}
                        cityGuides={cityGuideThemes}
                        themeGroups={themeGroups}
                        ungrouped={ungrouped}
                        ungroupedThemesHeading="Static themes"
                        onThemeEditClick={(theme) => {
                            goToThemeEdit(theme);
                        }}
                        onArticleClick={(theme, article) => {
                            goToArticleView(theme, article);
                        }}
                    />
                ) : (
                    <ThemeListLoading ungroupedThemesHeading="Static themes" />
                )}
            </List.Tab>

            {pathParams.mode === "preview" && (
                <FullScreenOverlay>
                    <Flex.Row
                        justifyContent="space-between"
                        className="content-themes__title"
                    >
                        <h2>Preview as</h2>

                        <Button.Ghost
                            onClick={() => {
                                returnToThemes();
                            }}
                        >
                            <Icon.Close.Medium.N10 />
                        </Button.Ghost>
                    </Flex.Row>
                    <PreviewThemes />
                </FullScreenOverlay>
            )}

            {pathParams.mode === "view" && (
                <WithGoogle>
                    {(googlePlaceService) =>
                        pathParams.themeId && pathParams.articleId ? (
                            <ViewCatalogItem
                                catalogItemId={pathParams.articleId}
                                currentAgent={currentAgent}
                                suppliers={suppliers || []}
                                googlePlaceService={googlePlaceService}
                                onClose={() => {
                                    returnToThemes();
                                }}
                                onEdit={(catalogItem) => {
                                    goToArticleEdit(pathParams.themeId!, catalogItem);
                                }}
                                onToggleArchive={() => {
                                    onArticleArchiveToggle();
                                }}
                            />
                        ) : (
                            <LoadingSpinner />
                        )
                    }
                </WithGoogle>
            )}

            {pathParams.mode === "edit" &&
                pathParams.themeId &&
                !pathParams.articleId && (
                    <EditTheme
                        themeId={pathParams.themeId}
                        onBack={() => {
                            returnToThemes();
                        }}
                        onDelete={() => {
                            void refreshThemes();
                            returnToThemes();
                        }}
                        onSave={(theme) => {
                            onThemeEdit(theme);
                        }}
                    />
                )}

            {pathParams.mode === "edit" &&
                pathParams.themeId &&
                pathParams.articleId && (
                    <EditCatalogItem
                        catalogItemId={pathParams.articleId}
                        currentAgent={currentAgent}
                        suppliers={suppliers || []}
                        onClose={() => {
                            goToArticleView(pathParams.themeId!, pathParams.articleId!);
                        }}
                        onSave={(item) => {
                            onArticleEdit(item);
                        }}
                    />
                )}

            {pathParams.mode === "create" && (
                <FullScreenOverlay>
                    <Flex.Row className="content-themes__title">
                        <h2>New theme</h2>
                    </Flex.Row>

                    <ThemeForm
                        theme={undefined}
                        onCancel={() => {
                            returnToThemes();
                        }}
                        onDelete={() => {
                            returnToThemes();
                        }}
                        onSave={(theme) => {
                            onThemeCreate(theme);
                        }}
                    />
                </FullScreenOverlay>
            )}
        </>
    );
}

export default ContentThemes;
